import { Injectable, signal, computed, inject, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of } from 'rxjs';
import { STORAGE_KEYS } from '../constants/storage-keys';
import type {
  AdminUser,
  LoginResponse,
  MfaChallengeResponse,
  RecoveryCodesResponse,
  ApiError,
} from '../models/auth.model';
import { isLoginResponse } from '../models/auth.model';
import { PermissionService } from './permission.service';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly permissions = inject(PermissionService);

  /** Sessions used to live in sessionStorage, which a new tab does not inherit. */
  private legacyMigrated = false;
  private readonly tokenSignal = signal<string | null>(this.getStoredToken());
  private readonly userSignal = signal<AdminUser | null>(this.getStoredUser());
  private expiryTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly onStorageEvent = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== null && event.key !== STORAGE_KEYS.ACCESS_TOKEN && event.key !== STORAGE_KEYS.USER) return;

    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    this.tokenSignal.set(token);
    this.userSignal.set(this.parseStoredUser(localStorage.getItem(STORAGE_KEYS.USER)));
    this.clearExpiryTimer();
    if (!token) {
      const path = this.router.url.split('?')[0];
      if (path !== '/login' && path !== '/verify-otp') {
        void this.router.navigate(['/login']);
      }
      return;
    }
    this.scheduleExpiryFromStoredToken();
  };

  readonly isAuthenticated = computed(() => !!this.tokenSignal());
  readonly user = computed(() => this.userSignal());

  constructor() {
    this.scheduleExpiryFromStoredToken();
    window.addEventListener('storage', this.onStorageEvent);
  }

  ngOnDestroy(): void {
    this.clearExpiryTimer();
    window.removeEventListener('storage', this.onStorageEvent);
  }

  getToken(): string | null {
    let t = this.tokenSignal();
    if (!t) {
      t = this.getStoredToken();
      if (t) this.tokenSignal.set(t);
    }
    return t ?? null;
  }

  login(email: string, password: string): Observable<LoginResponse | MfaChallengeResponse | ApiError> {
    return this.http
      .post<LoginResponse | MfaChallengeResponse>('api/v1/admin/auth/login', { email, password })
      .pipe(
        tap((res) => this.persistSessionIfPresent(res)),
        catchError((err) => of(err.error ?? { error: { code: 'UNKNOWN', message: err.message } }))
      );
  }

  /// v1.5: second step of MFA login — exchange the challenge token + emailed code
  /// (or a recovery code) for a session.
  verifyOtp(challengeToken: string, code: string): Observable<LoginResponse | ApiError> {
    return this.http
      .post<LoginResponse>('api/v1/admin/auth/verify-otp', { challenge_token: challengeToken, code })
      .pipe(
        tap((res) => this.persistSessionIfPresent(res)),
        catchError((err) => of(err.error ?? { error: { code: 'UNKNOWN', message: err.message } }))
      );
  }

  /// v1.5: regenerate the authenticated admin's single-use recovery codes.
  regenerateRecoveryCodes(): Observable<RecoveryCodesResponse | ApiError> {
    return this.http
      .post<RecoveryCodesResponse>('api/v1/admin/auth/recovery-codes', {})
      .pipe(catchError((err) => of(err.error ?? { error: { code: 'UNKNOWN', message: err.message } })));
  }

  private persistSessionIfPresent(res: LoginResponse | MfaChallengeResponse): void {
    if (!isLoginResponse(res)) return;
    this.tokenSignal.set(res.access_token);
    this.userSignal.set(res.user);
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, res.access_token);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(res.user));
    this.permissions.apply(res.permissions ?? [], !!res.is_super_admin || !!res.user.is_super_admin);
    this.scheduleExpiry(res.expires_in);
  }

  logout(): void {
    this.clearExpiryTimer();
    this.tokenSignal.set(null);
    this.userSignal.set(null);
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    this.permissions.clear();
    this.router.navigate(['/login']);
  }

  private scheduleExpiry(expiresInSeconds: number | undefined): void {
    this.clearExpiryTimer();
    if (!expiresInSeconds || expiresInSeconds <= 0) return;

    const bufferMs = 60_000;
    const ms = Math.max(expiresInSeconds * 1000 - bufferMs, 0);
    this.expiryTimer = setTimeout(() => this.logout(), ms);
  }

  private scheduleExpiryFromStoredToken(): void {
    const token = this.getStoredToken();
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const exp = payload.exp as number | undefined;
      if (!exp) return;
      const remainingMs = exp * 1000 - Date.now() - 60_000;
      if (remainingMs <= 0) {
        this.logout();
      } else {
        this.expiryTimer = setTimeout(() => this.logout(), remainingMs);
      }
    } catch {
      // malformed token; let the 401 interceptor handle it
    }
  }

  private clearExpiryTimer(): void {
    if (this.expiryTimer !== null) {
      clearTimeout(this.expiryTimer);
      this.expiryTimer = null;
    }
  }

  private getStoredToken(): string | null {
    this.migrateLegacySession();
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  private getStoredUser(): AdminUser | null {
    this.migrateLegacySession();
    return this.parseStoredUser(localStorage.getItem(STORAGE_KEYS.USER));
  }

  /** Copy a session saved by an older build so an already-open tab keeps working in new tabs. */
  private migrateLegacySession(): void {
    if (this.legacyMigrated) return;
    this.legacyMigrated = true;
    for (const key of [STORAGE_KEYS.ACCESS_TOKEN, STORAGE_KEYS.USER]) {
      const legacy = sessionStorage.getItem(key);
      if (legacy && !localStorage.getItem(key)) {
        localStorage.setItem(key, legacy);
      }
      sessionStorage.removeItem(key);
    }
  }

  private parseStoredUser(raw: string | null): AdminUser | null {
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AdminUser;
    } catch {
      return null;
    }
  }
}
